import { useEffect, useRef, useState } from 'react';
import { checkContactExists } from '@/services/users.service';

/** Contact channel used by the uniqueness check. */
export type ContactCheckType = 'email' | 'phone';

/** Params for {@link useContactExistsCheck}. */
interface UseContactExistsCheckParams {
  value: string;
  type: ContactCheckType;
  enabled?: boolean;
  excludeUserId?: string;
  skipHydratedValue?: boolean;
}

/** Debounce before calling the contact-exists API. */
const DEBOUNCE_MS = 600;

/** Map a form contact-type string to `email` or `phone`. */
export function resolveContactCheckType(contactType: string | undefined): ContactCheckType {
  if (contactType === 'phone') return 'phone';
  return 'email';
}

/** True when the value looks like an email worth sending to the uniqueness API. */
function isCheckableEmail(value: string): boolean {
  const at = value.indexOf('@');
  if (at <= 0 || at !== value.lastIndexOf('@')) return false;
  const domain = value.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  return !value.includes(' ') && dot > 0 && dot < domain.length - 1;
}

/** True when the value has enough digits to send to the uniqueness API. */
function isCheckablePhone(value: string): boolean {
  return value.replaceAll(/\D/g, '').length >= 6;
}

/** True when the contact value is complete enough to check uniqueness. */
function isCheckableContact(value: string, type: ContactCheckType): boolean {
  return type === 'email' ? isCheckableEmail(value) : isCheckablePhone(value);
}

/** Normalize a contact value for comparison (digits-only phone, lowercased email). */
function normalizeContactValue(value: string, type: ContactCheckType): string {
  if (type === 'phone') return value.replaceAll(/\D/g, '');
  return value.trim().toLowerCase();
}

/** Skip the uniqueness check while the value is still the hydrated original. */
function shouldSkipHydratedValue(
  skipHydratedValue: boolean,
  baselineRef: { current: string | null },
  value: string,
  type: ContactCheckType,
): boolean {
  if (!skipHydratedValue) return false;
  const normalized = normalizeContactValue(value, type);
  if (baselineRef.current === null) {
    baselineRef.current = normalized;
    return true;
  }
  return baselineRef.current === normalized;
}

/** Debounced uniqueness check for email/phone (skips the hydrated value). */
export function useContactExistsCheck({
  value,
  type,
  enabled = true,
  excludeUserId,
  skipHydratedValue = false,
}: UseContactExistsCheckParams): { isChecking: boolean; exists: boolean | null } {
  const [isChecking, setIsChecking] = useState(false);
  const [exists, setExists] = useState<boolean | null>(null);
  const hydratedBaselineRef = useRef<string | null>(null);
  const baselineKeyRef = useRef(excludeUserId);

  useEffect(() => {
    if (baselineKeyRef.current !== excludeUserId) {
      baselineKeyRef.current = excludeUserId;
      hydratedBaselineRef.current = null;
    }

    if (!enabled || !value.trim()) {
      setExists(null);
      setIsChecking(false);
      return;
    }

    if (!isCheckableContact(value, type)) {
      setExists(null);
      setIsChecking(false);
      return;
    }

    if (shouldSkipHydratedValue(skipHydratedValue, hydratedBaselineRef, value, type)) {
      setExists(null);
      setIsChecking(false);
      return;
    }

    let cancelled = false;
    setExists(null);
    setIsChecking(true);
    const timer = setTimeout(() => {
      void checkContactExists(value, type, excludeUserId)
        .then((result) => {
          if (!cancelled) setExists(result.exists);
        })
        .catch(() => {
          if (!cancelled) setExists(null);
        })
        .finally(() => {
          if (!cancelled) setIsChecking(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      setIsChecking(false);
    };
  }, [value, type, enabled, excludeUserId, skipHydratedValue]);

  return { isChecking, exists };
}
